import { useEffect, useRef, useState } from 'react';

import { Modal } from './Modal';
import { centredOffset, clampOffset, displayedSize, sourceRect, zoomedOffset } from '@/lib/crop';
import { cropToFile } from '@/lib/crop-canvas';

/** The square somebody drags the picture around inside. */
const VIEWPORT = 240;
/**
 * As far in as the slider goes.
 *
 * Four, because past that a phone photograph is being enlarged rather than
 * cropped: the square being kept is a quarter of the short side, which on a
 * 3000-pixel photo is 750 pixels drawn into 512 — the last point where the
 * stored picture is still made of real detail.
 */
const MAX_ZOOM = 4;
/** How far one arrow key moves the picture, in screen pixels. */
const STEP = 12;

/**
 * Choosing which square of a photograph becomes somebody's picture.
 *
 * The alternative was centre-cropping whatever arrives, which is what a round
 * frame with `object-fit: cover` does on its own — and it is wrong often
 * enough to matter: a photo of a person standing up, cropped down its middle,
 * shows a shirt.
 *
 * No cropping library. What one would add is this file and `crop.ts`: a
 * picture positioned inside a square with `overflow: hidden`, and the sum that
 * turns where it was left into pixels. The sum is the part that goes wrong,
 * and it is tested on its own.
 *
 * The frame is `Modal`, like every other dialog here. Written by hand the first
 * time, it lost Escape, closing on the backdrop, the focus that starts on the
 * first control, and the accent button — the last one silently, because the
 * class it named does not exist.
 */
export function AvatarCropper({
  file,
  saving,
  onCancel,
  onSave,
}: {
  file: File;
  saving: boolean;
  onCancel: () => void;
  onSave: (cropped: File) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const image = useRef<HTMLImageElement>(null);
  const from = useRef<{ x: number; y: number } | null>(null);
  const cropping = useRef(false);

  /*
   * An object URL, revoked when the picture goes. Reading the file as a data
   * URL would put a megabyte of base64 through React state on every render of
   * this component; the browser can hand out a handle instead.
   *
   * Everything about the last photo is cleared here as well as the URL. The
   * same cropper can be handed a second file — the dialog does not trap focus,
   * so the hidden file input behind it is still reachable — and a zoom of 4
   * left over from the last one opens the new picture magnified in a corner,
   * with the slider reading 4 and nothing having asked for it.
   */
  useEffect(() => {
    const made = URL.createObjectURL(file);
    setUrl(made);
    setNatural({ width: 0, height: 0 });
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setFailure(null);
    return () => URL.revokeObjectURL(made);
  }, [file]);

  const ready = natural.width > 0 && natural.height > 0;
  const shown = displayedSize(natural, VIEWPORT, zoom);

  const move = (to: { x: number; y: number }) =>
    setOffset(clampOffset(to, natural, VIEWPORT, zoom));

  /*
   * A step from wherever the picture is now, read through the updater rather
   * than from the render that installed the handler. Several key presses
   * inside one tick would otherwise each start from the same stale offset and
   * only the last would count.
   */
  const nudge = (step: { x: number; y: number }) =>
    setOffset((current) =>
      clampOffset({ x: current.x + step.x, y: current.y + step.y }, natural, VIEWPORT, zoom),
    );

  const changeZoom = (next: number) => {
    setOffset((current) => zoomedOffset(current, natural, VIEWPORT, zoom, next));
    setZoom(next);
  };

  const save = async () => {
    const element = image.current;
    /*
     * `cropping` rather than `saving`: the flag the caller passes only goes up
     * once the upload has started, and drawing the canvas happens before that.
     * Two quick presses would otherwise send the same photo twice and leave one
     * of the two files behind.
     */
    if (!element || !ready || cropping.current) return;

    cropping.current = true;
    try {
      setFailure(null);
      onSave(await cropToFile(element, sourceRect(natural, VIEWPORT, zoom, offset), 'avatar.jpg'));
    } catch (error) {
      // The browser refusing to draw is the only way here, and it has a
      // sentence of its own — better than a button that does nothing.
      setFailure((error as Error).message);
    } finally {
      cropping.current = false;
    }
  };

  return (
    <Modal
      title="Choose what shows"
      submitLabel="Use this photo"
      submitting={saving}
      canSubmit={ready}
      error={failure}
      onSubmit={() => void save()}
      onClose={onCancel}
      width={320}
    >
      <span className="text-[11.5px] leading-[1.5]" style={{ color: 'var(--muted)' }}>
        Drag the photo, or use the arrow keys, and the slider to zoom. The circle
        is what other people see.
      </span>

      {/*
        The circle is a mask over a square viewport, not the viewport itself:
        the saved photo is square, and cropping to a circle would bake the
        corners in as white for anywhere it is shown as a square.
      */}
      <div
        role="group"
        aria-label="The part of the photo to show"
        tabIndex={0}
        className="focus-ring relative touch-none self-center overflow-hidden rounded-full border"
        style={{
          width: VIEWPORT,
          height: VIEWPORT,
          borderColor: 'var(--line2)',
          background: 'var(--in)',
          cursor: dragging ? 'grabbing' : 'grab',
        }}
        onPointerDown={(event) => {
          // The primary button only. A right-click opening a context menu
          // would otherwise start a drag nobody can see themselves doing.
          if (event.button !== 0) return;
          from.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
          setDragging(true);
          /*
           * Optional, and not only for the tests: capture is what keeps the
           * drag alive when the cursor leaves the circle, but a browser that
           * does not have it — or has lost the pointer already — would throw
           * from inside the handler and abandon the drag it was starting.
           */
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          const start = from.current;
          if (!start) return;
          move({ x: event.clientX - start.x, y: event.clientY - start.y });
        }}
        onPointerUp={() => {
          from.current = null;
          setDragging(false);
        }}
        /*
         * A touch the system takes back — a long press opening a menu, a call
         * arriving — ends the gesture without a pointerup. Without this the
         * picture follows the cursor afterwards with nothing held down.
         */
        onPointerCancel={() => {
          from.current = null;
          setDragging(false);
        }}
        /*
         * Arrow keys, because dragging is the only way to frame a face and a
         * pointer is not the only way people work. Without them somebody on a
         * keyboard can zoom and nothing else — which on a portrait photograph
         * means they cannot reach the face at all.
         */
        onKeyDown={(event) => {
          const by: Record<string, { x: number; y: number }> = {
            ArrowLeft: { x: -STEP, y: 0 },
            ArrowRight: { x: STEP, y: 0 },
            ArrowUp: { x: 0, y: -STEP },
            ArrowDown: { x: 0, y: STEP },
          };
          const step = by[event.key];
          if (!step) return;
          event.preventDefault();
          nudge(step);
        }}
      >
        {url ? (
          <img
            ref={image}
            src={url}
            alt=""
            draggable={false}
            onLoad={(event) => {
              const loaded = {
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              };
              setNatural(loaded);
              // Centred rather than in a corner: the middle of a photograph is
              // where the subject usually is.
              setOffset(centredOffset(loaded, VIEWPORT, 1));
            }}
            /*
             * A file the browser cannot decode — a HEIC straight off an iPhone,
             * say, which `accept` does not stop because a file picker's filter
             * is a suggestion. Without this the dialog sits there with an empty
             * circle and a button that will not enable, saying nothing.
             */
            onError={() =>
              setFailure('This browser cannot read that image. Try a PNG, JPEG or WebP.')
            }
            style={{
              position: 'absolute',
              left: offset.x,
              top: offset.y,
              width: shown.width || VIEWPORT,
              height: shown.height || VIEWPORT,
              maxWidth: 'none',
            }}
          />
        ) : null}
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="field-label">Zoom</span>
        <input
          type="range"
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          disabled={!ready}
          onChange={(event) => changeZoom(Number(event.target.value))}
          className="focus-ring w-full"
        />
      </label>
    </Modal>
  );
}
